import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';

/**
 * One product tile, shared by the shop landing page and the category pages so the store
 * looks the same everywhere. Links to the product's own page: /shop/<category>/<product>.
 */
const productHref = (product) => {
  // seo_url is stored relative, e.g. "for-st-john-girls/ladies-muscle-tank", and the product
  // route is /shop/<category>/<product> - same rule the rest of the store uses.
  if (product.seo_url) {
    const path = product.seo_url.replace(/^\/+/, '');
    return path.startsWith('shop/') ? `/${path}` : `/shop/${path}`;
  }
  return `/product/${product.id}`;
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const ProductTile = ({ product, onAddToCart }) => (
  <article className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md" data-testid={`shop-product-${product.id}`}>
    <Link to={productHref(product)} className="block">
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        {product.image || product.images?.[0] ? (
          <img
            src={product.image || product.images[0]}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300"><ShoppingCart className="h-10 w-10" /></div>
        )}
        {product.original_price > product.price && (
          <span className="absolute left-2 top-2 rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold uppercase text-white">Sale</span>
        )}
        {!product.in_stock && (
          <span className="absolute right-2 top-2 rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] font-bold uppercase text-white">Sold out</span>
        )}
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 text-sm font-bold text-slate-900 sm:text-base">{product.name}</h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{product.category}</p>
        <p className="mt-2 flex items-baseline gap-2">
          <span className="text-lg font-black text-slate-900">{money(product.price)}</span>
          {product.original_price > product.price && (
            <span className="text-sm text-slate-400 line-through">{money(product.original_price)}</span>
          )}
        </p>
      </div>
    </Link>
    {onAddToCart && (
      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={() => onAddToCart(product)}
          disabled={!product.in_stock}
          className="flex w-full items-center justify-center gap-2 rounded-sm bg-blue-800 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid={`shop-add-to-cart-${product.id}`}
        >
          <ShoppingCart className="h-3.5 w-3.5" /> {product.in_stock ? 'Add to Cart' : 'Sold Out'}
        </button>
      </div>
    )}
  </article>
);

export { productHref, money };
export default ProductTile;
